/** Bidirectional mapping between legacy page keys and URL paths */

export interface NavCrumb {
  label: string
  page: string
  navigateOpts?: NavOpts
}

export interface NavOpts {
  batchId?: string | number
  applicationId?: string | number
  identId?: string
  recipientId?: string | number
  paymentJobId?: string | number
  userId?: string
  code?: string
  recipient?: { identId?: string }
  caseIndex?: number
  importBatchId?: number
  ocrCaseId?: number
  reviewParentPage?: string
  baseCrumb?: NavCrumb
  detailPageTitle?: string
  alert?: string
  returnPage?: string
  /**
   * ダッシュボードカードから受給者検索へ絞り込み遷移するための初期 filter。
   * pageToPath('recipient-search', { recipientSearchPreset: {...} }) で
   * `?dashboardPreset=...&welfare=...&statuses=...` 形式の query 文字列に変換される。
   */
  recipientSearchPreset?: {
    dashboardPreset?: 'overallPending'
    welfare?: 'all' | 'active' | 'ended'
    statuses?: string[]
    /** No.27: 事務局確認用ステータス絞り込み。0=未設定(NULL) / 正の値=master.id。 */
    officeReviewStatusId?: number
  }
  /**
   * TopMenu の業務メニューからのフレッシュスタート用フラグ。
   * NavigationContext が react-router の navigate state にこのフラグを乗せ、
   * target ページ側（例：RecipientSearch）が sessionStorage 由来の
   * 前回検索条件を破棄して DEFAULT 状態で再初期化する。URL には反映しない。
   */
  resetSearchPersistence?: boolean
}

const PAGE_TO_PATH: Record<string, string> = {
  'home': '/',
  'submenu-report': '/menu/report',
  'submenu-admin': '/menu/admin',
  'application-register': '/application/register',
  'application-review': '/application/review',
  'application-doc-detail': '/application/doc-detail',
  'application-doc-edit': '/application/doc-edit',
  'application-document-detail': '/application/documents',
  'application-document-edit': '/application/documents/edit',
  'application-document-new': '/application/documents/new',
  'data-import': '/data-import',
  'import-error-edit': '/data-import/errors',
  'ocr-read-import': '/ocr-read-import',
  'ocr-read-list': '/ocr-read-list',
  'recipient-search': '/recipient/search',
  'recipient-detail': '/recipient/detail',
  'recipient-edit': '/recipient/edit',
  'recipient-new': '/recipient/new',
  'bank-transfer': '/export/bank-transfer',
  'notification-output': '/export/notification',
  'business-report': '/report',
  'payment-job': '/payment-job',
  'payment-job-create': '/payment-job/create',
  'payment-job-detail': '/payment-job/detail',
  'user-management': '/admin/users',
  'user-edit': '/admin/users/edit',
  'user-create': '/admin/users/create',
  'log-operation-history': '/admin/log',
  'release-history': '/admin/releases',
  'ocr-success-stats': '/admin/ocr-success-stats',
  'municipality-management': '/admin/municipalities',
  'municipality-edit': '/admin/municipalities/edit',
  'municipality-create': '/admin/municipalities/create',
  'municipality-ocr-mapping': '/admin/municipalities/ocr-mapping',
  'municipality-moushidesho-ocr-mapping': '/admin/municipalities/moushidesho-ocr-mapping',
  'announcement-management': '/admin/announcements',
  'office-review-status-management': '/admin/office-review-statuses',
  'notice-settings-management': '/admin/notice-settings',
  'transfer-dat-settings-management': '/admin/transfer-dat-settings',
  'privacy-policy-settings-management': '/admin/privacy-policy-settings',
  'recipient-csv-column-settings': '/admin/recipient-csv-columns',
  'login': '/login',
  'logout': '/logout',
}

const DYNAMIC_PATH_MATCHERS: Array<{ page: string; pattern: RegExp }> = [
  { page: 'import-error-edit', pattern: /^\/data-import\/errors\/[^/]+$/ },
  { page: 'application-document-new', pattern: /^\/application\/documents\/new\/[^/]+$/ },
  { page: 'application-document-edit', pattern: /^\/application\/documents\/[^/]+\/edit$/ },
  { page: 'application-document-detail', pattern: /^\/application\/documents\/[^/]+$/ },
  { page: 'payment-job-detail', pattern: /^\/payment-job\/detail\/[^/]+$/ },
  { page: 'recipient-detail', pattern: /^\/recipient\/detail\/[^/]+$/ },
  { page: 'recipient-detail', pattern: /^\/recipient\/detail\/ident\/[^/]+$/ },
  { page: 'recipient-edit', pattern: /^\/recipient\/edit\/[^/]+$/ },
  { page: 'user-edit', pattern: /^\/admin\/users\/edit\/[^/]+$/ },
  { page: 'payment-job-create', pattern: /^\/payment-job\/create\/[^/]+$/ },
  { page: 'municipality-edit', pattern: /^\/admin\/municipalities\/edit\/[^/]+$/ },
  { page: 'municipality-ocr-mapping', pattern: /^\/admin\/municipalities\/ocr-mapping\/[^/]+$/ },
  { page: 'municipality-moushidesho-ocr-mapping', pattern: /^\/admin\/municipalities\/moushidesho-ocr-mapping\/[^/]+$/ },
  { page: 'application-review', pattern: /^\/application\/review\/[^/]+\/[^/]+\/[^/]+$/ },
]

const PATH_TO_PAGE: Record<string, string> = {}
for (const [page, path] of Object.entries(PAGE_TO_PATH)) {
  PATH_TO_PAGE[path] = page
}

export function pageToPath(pageKey: string, opts?: NavOpts): string {
  if (pageKey === 'import-error-edit' && opts && opts.batchId != null) {
    return `/data-import/errors/${encodeURIComponent(String(opts.batchId))}`
  }

  if ((pageKey === 'application-document-detail' || pageKey === 'application-doc-detail') && opts) {
    const applicationId =
      typeof opts.applicationId === 'number' && Number.isFinite(opts.applicationId)
        ? String(opts.applicationId)
        : ''
    if (applicationId) {
      return `/application/documents/${encodeURIComponent(applicationId)}`
    }

    const identId = typeof opts.identId === 'string' ? opts.identId.trim() : ''
    if (identId) {
      return `/application/documents?identId=${encodeURIComponent(identId)}`
    }
  }

  if (pageKey === 'application-document-new' && opts) {
    const identId = typeof opts.identId === 'string' ? opts.identId.trim() : ''
    if (identId) {
      return `/application/documents/new/${encodeURIComponent(identId)}`
    }
  }

  if ((pageKey === 'application-document-edit' || pageKey === 'application-doc-edit') && opts) {
    const applicationId =
      typeof opts.applicationId === 'number' && Number.isFinite(opts.applicationId)
        ? String(opts.applicationId)
        : ''
    if (applicationId) {
      return `/application/documents/${encodeURIComponent(applicationId)}/edit`
    }
  }

  if (pageKey === 'recipient-detail' && opts) {
    const recipientId =
      typeof opts.recipientId === 'number' && Number.isFinite(opts.recipientId)
        ? String(opts.recipientId)
        : ''
    const identId =
      typeof opts.identId === 'string'
        ? opts.identId.trim()
        : typeof opts.recipient?.identId === 'string'
        ? opts.recipient.identId.trim()
        : ''

    if (recipientId) {
      return `/recipient/detail/${encodeURIComponent(recipientId)}`
    }
    if (identId) {
      return `/recipient/detail/ident/${encodeURIComponent(identId)}`
    }
  }

  if (pageKey === 'recipient-edit' && opts) {
    const recipientId =
      typeof opts.recipientId === 'number' && Number.isFinite(opts.recipientId)
        ? String(opts.recipientId)
        : ''
    if (recipientId) {
      return `/recipient/edit/${encodeURIComponent(recipientId)}`
    }
  }

  if (pageKey === 'payment-job-detail' && opts) {
    const paymentJobId =
      typeof opts.paymentJobId === 'number' && Number.isFinite(opts.paymentJobId)
        ? String(opts.paymentJobId)
        : ''
    if (paymentJobId) {
      return `/payment-job/detail/${encodeURIComponent(paymentJobId)}`
    }
  }

  if (pageKey === 'payment-job-create' && opts) {
    const paymentJobId =
      typeof opts.paymentJobId === 'number' && Number.isFinite(opts.paymentJobId)
        ? String(opts.paymentJobId)
        : ''
    if (paymentJobId) {
      return `/payment-job/create/${encodeURIComponent(paymentJobId)}`
    }
  }

  if (pageKey === 'user-edit' && opts) {
    const userId = typeof opts.userId === 'string' ? opts.userId.trim() : ''
    if (userId) {
      return `/admin/users/edit/${encodeURIComponent(userId)}`
    }
  }

  if (pageKey === 'municipality-edit' && opts) {
    const code = typeof opts.code === 'string' ? opts.code.trim() : ''
    if (code) {
      return `/admin/municipalities/edit/${encodeURIComponent(code)}`
    }
  }

  if (pageKey === 'municipality-ocr-mapping' && opts) {
    const code = typeof opts.code === 'string' ? opts.code.trim() : ''
    if (code) {
      return `/admin/municipalities/ocr-mapping/${encodeURIComponent(code)}`
    }
  }

  if (pageKey === 'municipality-moushidesho-ocr-mapping' && opts) {
    const code = typeof opts.code === 'string' ? opts.code.trim() : ''
    if (code) {
      return `/admin/municipalities/moushidesho-ocr-mapping/${encodeURIComponent(code)}`
    }
  }

  if (pageKey === 'application-review' && opts) {
    const batchId = typeof opts.importBatchId === 'number' && Number.isInteger(opts.importBatchId) && opts.importBatchId > 0
      ? String(opts.importBatchId)
      : ''
    const caseId = typeof opts.ocrCaseId === 'number' && Number.isInteger(opts.ocrCaseId) && opts.ocrCaseId > 0
      ? String(opts.ocrCaseId)
      : ''
    const idx = typeof opts.caseIndex === 'number' && Number.isInteger(opts.caseIndex) && opts.caseIndex >= 0
      ? String(opts.caseIndex)
      : '0'
    let path = PAGE_TO_PATH[pageKey] || '/'
    if (batchId && caseId) {
      path = `${path}/${batchId}/${caseId}/${idx}`
    }
    if (opts.reviewParentPage === 'recipient-search') {
      path = `${path}?parent=recipient-search`
    }
    return path
  }

  if (pageKey === 'recipient-search' && opts?.recipientSearchPreset) {
    const preset = opts.recipientSearchPreset
    const params: string[] = []
    if (preset.dashboardPreset) {
      params.push(`dashboardPreset=${encodeURIComponent(preset.dashboardPreset)}`)
    }
    // welfare='all' も明示的に URL に乗せる。受け側 applyUrlPreset() がダッシュボード遷移を
    // 判定する手掛かりになり、sessionStorage の残留検索条件を漏れなくリセットできる。
    if (preset.welfare) {
      params.push(`welfare=${encodeURIComponent(preset.welfare)}`)
    }
    if (preset.statuses && preset.statuses.length > 0) {
      params.push(`statuses=${preset.statuses.map(encodeURIComponent).join(',')}`)
    }
    // 0（未設定）も明示的に URL に乗せるため null/undefined のみ除外する。
    if (preset.officeReviewStatusId != null) {
      params.push(`officeReviewStatusId=${encodeURIComponent(String(preset.officeReviewStatusId))}`)
    }
    const base = PAGE_TO_PATH[pageKey] || '/'
    return params.length > 0 ? `${base}?${params.join('&')}` : base
  }

  return PAGE_TO_PATH[pageKey] || '/'
}

export function pathToPage(path: string): string {
  if (PATH_TO_PAGE[path]) {
    return PATH_TO_PAGE[path]
  }

  const dynamicMatch = DYNAMIC_PATH_MATCHERS.find(({ pattern }) => pattern.test(path))
  if (dynamicMatch) {
    return dynamicMatch.page
  }

  return 'home'
}

export interface ApplicationReviewLocation {
  importBatchId?: number
  ocrCaseId?: number
  caseIndex?: number
  reviewParentPage?: string
}

export function parseApplicationReviewLocation(pathname: string, search: string): ApplicationReviewLocation {
  const out: ApplicationReviewLocation = {}
  const match = pathname.match(/^\/application\/review\/(\d+)\/(\d+)\/(\d+)$/)
  if (match) {
    const batchId = Number(match[1])
    const caseId = Number(match[2])
    const idx = Number(match[3])
    if (batchId > 0 && caseId > 0 && idx >= 0) {
      out.importBatchId = batchId
      out.ocrCaseId = caseId
      out.caseIndex = idx
    }
  }
  const params = new URLSearchParams(search)
  if (params.get('parent') === 'recipient-search') out.reviewParentPage = 'recipient-search'
  return out
}

export { PAGE_TO_PATH }
